import { get } from 'lodash'
import { runWhenCookiebotConsentGranted } from '~/app/cookiebot'
import { trackHeurekaProductDetail } from '~/sites/cvb/heureka/directConversions'
import CartDiscountPriceMixin from '~/themes/cvb/mixins/CartDiscountPriceMixin'
import MarketingEmbedMixin from '~/themes/cvb/mixins/MarketingEmbedMixin'
import PageProductDetail from '~/themes/base/components/product/PageProductDetail/PageProductDetail.vue'
import contentSettings from '@site/content.settings'

const MANUFACTURER_PARAMETER_ID = '65e70b8cacf5480017f43467'

export default {
  extends: PageProductDetail,
  mixins: [MarketingEmbedMixin, CartDiscountPriceMixin],
  mounted() {
    this.heurekaProductDetailConsentDisposer = runWhenCookiebotConsentGranted(contentSettings, ['marketing'], () => {
      trackHeurekaProductDetail(this)
    })
  },
  beforeDestroy() {
    if (typeof this.heurekaProductDetailConsentDisposer === 'function') {
      this.heurekaProductDetailConsentDisposer()
      this.heurekaProductDetailConsentDisposer = null
    }
  },
  computed: {
    displayedDescriptionHtml() {
      return this.getConsentAwareEmbedHtml(this.displayedDescription)
    },
    showOfferDateOfStorage() {
      const delayDays = Number(this.product.availability.delayDays)
      return (
        this.$themeSettings.components.PageProductDetail.showDateOfStorage &&
        Number.isFinite(delayDays) &&
        delayDays > 0
      )
    },
    showOfferAvailability() {
      return this.$themeSettings.components.PageProductDetail.showAvailability
    },
    showOfferDeliveryOptions() {
      return (
        this.$themeSettings.components.PageProductDetail.showDeliveryOptions &&
        this.displayedVariant.availability.isAvailable &&
        this.$store.state.cart.isLoaded &&
        this.product.availability.delayDays < 21
      )
    },
    availableLocalizedFiles() {
      return this.product.files.filter(file => file.locale === this.$i18n.locale)
    },
    hasOriginalPrice() {
      const originalWithTax = Number(get(this.displayedVariant, 'prices.originalWithTax', 0))
      const unitWithTax = Number(get(this.displayedVariant, 'prices.unitWithTax', 0))
      const originalWithoutTax = Number(get(this.displayedVariant, 'prices.originalWithoutTax', 0))
      const unitWithoutTax = Number(get(this.displayedVariant, 'prices.unitWithoutTax', 0))

      return originalWithTax > unitWithTax || originalWithoutTax > unitWithoutTax
    },
    schemaCategoryFromBreadcrumb() {
      if (!this.breadcrumbs || this.breadcrumbs.length === 0) {
        return this.product.categoryName || null
      }

      const last = this.breadcrumbs[this.breadcrumbs.length - 1]
      const beforeLast = this.breadcrumbs[this.breadcrumbs.length - 2]

      const isLastProduct = last.path === this.productPath || last.title === this.product.name

      if (isLastProduct && beforeLast) {
        return beforeLast.title
      }

      return last.title || this.product.categoryName || null
    },
    brand() {
      const manufacturerParam = this.parameters.find(p => p.entityId === MANUFACTURER_PARAMETER_ID)

      if (
        manufacturerParam &&
        Array.isArray(manufacturerParam.valueEntities) &&
        manufacturerParam.valueEntities.length > 0
      ) {
        return manufacturerParam.valueEntities[0].title || null
      }

      const manufacturerByTitle = this.parameters.find(
        p => p.entity && (p.entity.title === 'Výrobce' || p.entity.slug === 'vyrobce'),
      )

      if (
        manufacturerByTitle &&
        Array.isArray(manufacturerByTitle.valueEntities) &&
        manufacturerByTitle.valueEntities.length > 0
      ) {
        return manufacturerByTitle.valueEntities[0].title || null
      }

      if (this.productBrands && this.productBrands.length) {
        return this.productBrands[0].name || null
      }

      return null
    },
    manufacturerParam() {
      return (this.parameters || []).find(p => p.entityId === MANUFACTURER_PARAMETER_ID)
    },
    manufacturerName() {
      const p = this.manufacturerParam
      if (p?.valueEntities?.length) return p.valueEntities[0].title || null
      if (this.productBrands?.length) return this.productBrands[0].name || null
      return null
    },
    manufacturerEmail() {
      const p = this.manufacturerParam
      const txt = p?.valueEntities?.[0]?.description_short || ''
      return this.extractEmail(txt)
    },
    manufacturerWebsite() {
      const p = this.manufacturerParam
      const txt = p?.valueEntities?.[0]?.description_short || ''

      const websites = this.extractWebsite(txt)
      return Array.isArray(websites) && websites.length ? websites[0] : null
    },
  },
  head() {
    const baseHead = this.getSeoHead ? this.getSeoHead({ data: this.getProductSeoData() }) : {}

    if (!this.product || !this.displayedVariant) {
      return baseHead
    }

    const baseUrl = (this.$env && this.$env.STOREFRONT_URL ? this.$env.STOREFRONT_URL.replace(/\/$/, '') : '') || ''
    const url = this.resolveCanonicalUrl()

    const images = []
    if (this.product.mainImage) {
      images.push(
        this.imageUrl({
          src: this.product.mainImage,
          width: 1200,
        }),
      )
    }

    const stripHtml = html => (html || '').replace(/<[^>]*>?/gm, '')
    const description = this.displayedPerex || stripHtml(this.displayedDescription) || this.product.perex || ''

    const offer = {
      '@type': 'Offer',
      url,
      price:
        this.displayedVariant.prices && this.displayedVariant.prices.unitWithTax != null
          ? String(this.displayedVariant.prices.unitWithTax)
          : undefined,
      priceCurrency:
        (this.displayedVariant.prices && this.displayedVariant.prices.currencyCode) ||
        (this.$store.state.globals.currency && this.$store.state.globals.currency.code) ||
        'CZK',
      availability: (this.schemaAvailabilityUrl || '').replace('http://', 'https://'),
      itemCondition: 'https://schema.org/NewCondition',
    }

    // AggregateRating + Review
    let aggregateRating
    let reviews = []

    if (
      this.productReviews &&
      this.productReviews.meta &&
      this.productReviews.meta.custom &&
      this.productReviews.meta.custom.summary
    ) {
      const summary = this.productReviews.meta.custom.summary
      if (summary.totalCount > 0 && summary.totalRating) {
        aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: String(summary.totalRating),
          reviewCount: String(summary.totalCount),
        }
      }
    }

    if (this.productReviews && Array.isArray(this.productReviews.data)) {
      reviews = this.productReviews.data.slice(0, 10).map(review => ({
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: review.name || 'Zákazník',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: String(review.rating || 5),
          bestRating: '5',
          worstRating: '1',
        },
        reviewBody: review.text || review.message || '',
        datePublished: review.created_at || review.createdAt || undefined,
      }))
    }

    let brand
    if (this.brand && typeof this.brand === 'string') {
      brand = { '@type': 'Brand', name: this.brand }
    } else if (this.productBrands && this.productBrands.length && this.productBrands[0].name) {
      brand = { '@type': 'Brand', name: this.productBrands[0].name }
    }

    // Product JSON-LD
    const productJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: this.product.name,
      image: this.product.mainImage ? this.imageUrl({ src: this.product.mainImage, width: 2000 }) : undefined,
      description: this.displayedPerex || this.product.description || undefined,
      sku: this.catalogCode || undefined,
      gtin: this.product.EAN || undefined,
      brand: brand || undefined,
      category: this.schemaCategoryFromBreadcrumb || undefined,
      offers: {
        '@type': 'Offer',
        price: String(this.displayedVariant.prices.unitWithTax),
        priceCurrency: this.displayedVariant.prices.currencyCode,
        availability: this.schemaAvailabilityUrl,
        url,
      },
    }

    // BreadcrumbList
    const breadcrumbItems = (this.breadcrumbs || []).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title || item.label || item.name,
      item: this.canonicalLinkUrl
        ? this.canonicalLinkUrl({ path: item.path || item.to || '/' })
        : baseUrl + (item.path || item.to || '/'),
    }))

    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    }

    const clean = obj => JSON.parse(JSON.stringify(obj))

    const extraScripts = [
      {
        hid: 'ld-json-product',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(clean(productJsonLd)),
      },
      {
        hid: 'ld-json-breadcrumb',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(clean(breadcrumbLd)),
      },
    ]

    const existingScripts = baseHead.script || []
    const existingSanitizers = baseHead.__dangerouslyDisableSanitizers || []
    const sanitizers = existingSanitizers.includes('script') ? existingSanitizers : [...existingSanitizers, 'script']

    return {
      ...baseHead,
      script: [...existingScripts, ...extraScripts],
      __dangerouslyDisableSanitizers: sanitizers,
    }
  },
  methods: {
    getProductSeoData() {
      if (!this.product) {
        return {}
      }

      const description =
        this.displayedPerex ||
        this.product.perex ||
        this.stripHtml(this.displayedDescription) ||
        this.product.description ||
        this.product.description_short ||
        this.product.description_long ||
        ''

      return {
        ...this.product,
        title: this.product.title || this.product.name,
        description,
      }
    },
    extractEmail(text) {
      if (!text) return null
      const match = String(text).match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)
      return match ? match[0] : null
    },
    extractWebsite(text) {
      if (!text) return []

      const matches = String(text).match(/\b((https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+([\/?#][^\s]*)?)\b/gi) || []

      const normalized = matches
        .map(s => s.trim())
        .map(s => {
          if (!s) return null
          if (s.includes('@')) return null
          if (/^https?:\/\//i.test(s)) return s
          return `https://${s.replace(/^\/+/, '')}`
        })
        .filter(Boolean)

      return this.unique(normalized, v => v.toLowerCase())
    },
    unique(list, keyFn = v => v) {
      const out = []
      const seen = new Set()
      for (const item of list) {
        const key = keyFn(item)
        if (seen.has(key)) continue
        seen.add(key)
        out.push(item)
      }
      return out
    },
    stripHtml(html) {
      return String(html || '').replace(/<[^>]*>?/gm, '')
    },
    baseUrl() {
      const url = this.$env?.STOREFRONT_URL || ''
      return String(url).replace(/\/$/, '')
    },
    resolveCanonicalUrl() {
      const relative = this.displayedVariant?.relativeUrl || this.product?.relativeUrl || this.$route?.path || ''
      return this.canonicalLinkUrl ? this.canonicalLinkUrl({ path: relative }) : this.baseUrl() + relative
    },
  },
}
